import { Paperclip, Send, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { type ReactNode, useRef, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Kbd } from "@/components/ui/kbd";
import { Textarea } from "@/components/ui/textarea";
import {
	RECOGNITION_ACCEPT,
	RECOGNITION_ALLOWED_TYPES,
	RECOGNITION_MAX_FILE_SIZE,
} from "@/lib/recognition/document-types";

interface ChatInputProps {
	input: string;
	setInput: (value: string) => void;
	onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
	isLoading: boolean;
	attachedFile: File | null;
	setAttachedFile: (file: File | null) => void;
}

export function ChatInput({
	input,
	setInput,
	onSubmit,
	isLoading,
	attachedFile,
	setAttachedFile,
}: ChatInputProps) {
	const t = useTranslations("aiBuilder");
	const textareaRef = useRef<HTMLTextAreaElement | null>(null);
	const isSubmittingRef = useRef(false);
	const [isMultiline, setIsMultiline] = useState(false);
	const hasText = input.trim().length > 0;
	const canSend = Boolean(input.trim() || attachedFile);
	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const [fileError, setFileError] = useState<string | null>(null);
	const showFileHint = !(attachedFile || fileError);

	// Скрепка: один документ, до 20 МБ (t_aba782d3). Текст извлекает сервер.
	const handleFilePick = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0] || null;
		event.target.value = "";
		if (!file) {
			return;
		}
		if (!RECOGNITION_ALLOWED_TYPES.includes(file.type)) {
			setFileError(t("errors.fileType"));
			return;
		}
		if (file.size > RECOGNITION_MAX_FILE_SIZE) {
			setFileError(t("errors.fileTooLarge"));
			return;
		}
		setFileError(null);
		setAttachedFile(file);
	};

	const autoResize = () => {
		const el = textareaRef.current;
		if (!el) return;
		el.style.height = "auto";
		const maxHeight = 160;
		el.style.height = Math.min(el.scrollHeight, maxHeight) + "px";
		const minHeight = 44;
		const currentHeight = Number.parseFloat(el.style.height || "0");
		setIsMultiline(currentHeight > minHeight + 1);
	};

	const handleLocalSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		if (isLoading || isSubmittingRef.current) {
			e.preventDefault();
			return;
		}
		isSubmittingRef.current = true;
		onSubmit(e);
		setTimeout(() => {
			isSubmittingRef.current = false;
		}, 300);
	};

	return (
		<div className="supports-[padding:max(0px)]/ios sticky bottom-0 z-10 flex touch-manipulation flex-col gap-2 bg-gradient-to-t from-background via-background/90 to-transparent p-2 pt-3 pb-[max(0px,env(safe-area-inset-bottom))] backdrop-blur md:gap-3 md:p-3 md:pt-4">
			{showFileHint && (
				<div className="mx-auto flex w-full max-w-3xl items-center gap-1.5 px-2 text-muted-foreground text-xs">
					<Paperclip aria-hidden="true" className="size-3 shrink-0" />
					<span>{t("input.fileHint")}</span>
				</div>
			)}
			{(attachedFile || fileError) && (
				<div className="mx-auto flex w-full max-w-3xl flex-wrap items-center gap-2 px-2 text-muted-foreground text-xs">
					{attachedFile && (
						<Badge className="gap-1" variant="secondary">
							<Paperclip aria-hidden="true" className="size-3" />
							{attachedFile.name}
							<button
								aria-label={t("input.removeFile")}
								className="ml-1 hover:text-destructive"
								onClick={() => setAttachedFile(null)}
								type="button"
							>
								<X aria-hidden="true" className="size-3" />
							</button>
						</Badge>
					)}
					{attachedFile && <span>{t("input.attachHint")}</span>}
					{fileError && <span className="text-destructive">{fileError}</span>}
				</div>
			)}
			<form
				className="relative mx-auto flex w-full max-w-3xl items-center gap-2 rounded-2xl border border-border bg-card px-2 py-2 transition-all focus-within:border-primary/30 md:gap-3 md:rounded-xl md:px-4"
				onSubmit={handleLocalSubmit}
			>
				<input
					accept={RECOGNITION_ACCEPT}
					aria-label={t("input.attach")}
					className="hidden"
					onChange={handleFilePick}
					ref={fileInputRef}
					type="file"
				/>
				<Button
					aria-label={t("input.attach")}
					className="shrink-0"
					disabled={isLoading}
					onClick={() => fileInputRef.current?.click()}
					size="icon"
					title={t("input.attach")}
					type="button"
					variant="ghost"
				>
					<Paperclip className="size-4" />
				</Button>
				<Textarea
					aria-describedby="chat-input-help"
					aria-label={t("input.label")}
					autoCapitalize="sentences"
					autoComplete="off"
					autoCorrect="on"
					className="max-h-[10rem] min-h-[2.75rem] flex-1 resize-none whitespace-pre-wrap break-words border-none bg-transparent p-3 pr-14 text-base leading-normal shadow-none outline-none focus-visible:ring-0 focus-visible:ring-offset-0 md:pr-12 md:text-sm md:leading-snug"
					disabled={isLoading}
					enterKeyHint="send"
					id="chat-message"
					inputMode="text"
					name="message"
					onChange={(e) => {
						setInput(e.target.value);
						autoResize();
					}}
					onKeyDown={(e) => {
						if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
							e.preventDefault();
							(e.target as HTMLTextAreaElement).form?.requestSubmit();
							return;
						}
						if (e.key === "Enter" && !e.shiftKey) {
							e.preventDefault();
							(e.target as HTMLTextAreaElement).form?.requestSubmit();
						}
					}}
					placeholder={t("input.placeholder")}
					ref={textareaRef}
					rows={1}
					value={input}
				/>
				{}
				<Button
					aria-label={t("input.send")}
					className={`absolute right-2 transition-all duration-200 ease-out md:hidden ${
						isMultiline && hasText ? "bottom-2" : "top-1/2 -translate-y-1/2"
					}`}
					disabled={isLoading || !canSend}
					loading={isLoading}
					size="icon-lg"
					title={t("input.send")}
					type="submit"
				>
					{!isLoading && <Send className="size-5" />}
				</Button>
				{}
				<Button
					aria-label={t("input.send")}
					className={`absolute right-3 hidden transition-all duration-200 ease-out md:inline-flex ${
						isMultiline && hasText ? "bottom-2" : "top-1/2 -translate-y-1/2"
					}`}
					disabled={isLoading || !canSend}
					loading={isLoading}
					size="icon"
					title={t("input.send")}
					type="submit"
				>
					{!isLoading && <Send className="size-4" />}
				</Button>
			</form>
			<div
				className="mx-auto w-full max-w-3xl gap-2 px-2 text-center text-[0.6875rem] text-muted-foreground md:px-0 md:text-xs"
				id="chat-input-help"
			>
				{t.rich("input.help", { Kbd: (c: ReactNode) => <Kbd>{c}</Kbd> })}
			</div>
		</div>
	);
}
